import { useCallback, useMemo, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useMaquinasEquipItensVerificacaoPagination } from "../hooks/MaquinasEquipItens_verificacao/pagination";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { InspecaoMaquinaEquipDetalhes } from "../../domain/models/inspecao_maquinas_equip_detalhes";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InspecaoMaquinaEquipForm, InspecaoMaquinaEquipSchema } from "../schemas/MaquinasEquipItens_verificacao/inspecao_maquina_equip";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";

/**
 * LoadInspecaoMaquinasEquipFuncState
 */

type LoadInspecaoMaquinasEquipFuncState = {
  idControleCentroCusto?: number;
  idInspecaoMaquinaEquip?: number;
};

/**
 * LoadInspecaoMaquinasEquipItensFuncState
 */

type LoadInspecaoMaquinasEquipItensFuncState = {
  idControleCentroCusto?: number;
  idInspecaoMaquinaEquip?: number;
  page?: number;
  rowsPerPage?: number;
}

/**
 * useMaquinasEquipItensVerificacaoViewModel
 */

export const useMaquinasEquipItensVerificacaoViewModel = () => {
  const {
    controlePatrimonio: {
      getInspecaoMaquinasEquipByIdUseCase,
      getAllInspecaoMaquinasEquipItemUseCase,
      updateInspecaoMaquinasEquipDetailUseCase,
      downloadInspecaoMaquinaEquipArquivoUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
  } = useDependencyInjection();

  const {
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idControleCentroCusto, setIdControleCentroCusto] = useState<number>();
  const [idInspecaoMaquinaEquip, setIdInspecaoMaquinaEquip] = useState<number>();

  const [inspecaoMaquinaEquip, setInspecaoMaquinaEquip] = useState<InspecaoMaquinaEquipDetalhes>();

  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();

  const hookForm = useForm<InspecaoMaquinaEquipForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(InspecaoMaquinaEquipSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const pagination = useMaquinasEquipItensVerificacaoPagination();

  const [inspecaoMaquinaEquipItensHook] = hookForm.watch(['itens']);

  const statusOptions = useMemo(() => {
    return [
      { label: "Em Aberto", id: 0 },
      { label: "Encerrada", id: 1 },
    ];
  }, [
  ]);

  const funcionariosInspecionadoDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (inspecaoMaquinaEquip) {
        if (x.idFuncionario === inspecaoMaquinaEquip.idFuncionarioInspecionado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    inspecaoMaquinaEquip,
    funcionariosDropdown,
  ]);

  const funcionariosAprovadoDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (inspecaoMaquinaEquip) {
        if (x.idFuncionario === inspecaoMaquinaEquip.idFuncionarioAprovado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    inspecaoMaquinaEquip,
    funcionariosDropdown,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadInspecaoMaquinasEquip = useCallback(async (fs?: LoadInspecaoMaquinasEquipFuncState) => {
    try {
      const filteredIdControleCentroCusto = fs?.idControleCentroCusto ?? idControleCentroCusto;
      const filteredIdInspecaoMaquinaEquip = fs?.idInspecaoMaquinaEquip ?? idInspecaoMaquinaEquip;

      const result = await getInspecaoMaquinasEquipByIdUseCase.getInspecaoMaquinasEquipById(
        filteredIdControleCentroCusto,
        filteredIdInspecaoMaquinaEquip,
      );

      hookForm.setValue('idFuncionarioInspecionado', result.idFuncionarioInspecionado);
      hookForm.setValue('idFuncionarioAprovado', result.idFuncionarioAprovado);
      hookForm.setValue('status', result.status);
      hookForm.setValue('dataEncerramento', result.dataEncerramento);
      hookForm.setValue('descricaoProblema', result.descricaoProblema);
      hookForm.setValue('correcaoProblema', result.correcaoProblema);
      hookForm.setValue('nomearquivo1', result.nomearquivo1);
      hookForm.setValue('nomearquivo2', result.nomearquivo2);
      hookForm.setValue('arquivo1', null);
      hookForm.setValue('arquivo2', null);
      hookForm.setValue('limparArquivo1', false);
      hookForm.setValue('limparArquivo2', false);

      setInspecaoMaquinaEquip(result);
    } catch {
      setInspecaoMaquinaEquip(undefined);
      throw new Error("Falha ao buscar itens da inspeção!");
    }
  }, [
    idControleCentroCusto,
    idInspecaoMaquinaEquip,
    getInspecaoMaquinasEquipByIdUseCase,
    hookForm,
  ]);

  const loadInspecaoMaquinasEquipItens = useCallback(async (fs?: LoadInspecaoMaquinasEquipItensFuncState) => {
    try {
      const filteredIdControleCentroCusto = fs?.idControleCentroCusto ?? idControleCentroCusto;
      const filteredIdInspecaoMaquinaEquip = fs?.idInspecaoMaquinaEquip ?? idInspecaoMaquinaEquip;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllInspecaoMaquinasEquipItemUseCase.getAllInspecaoMaquinasEquipItem(
        filteredIdControleCentroCusto,
        filteredIdInspecaoMaquinaEquip,
        filteredPage,
        filteredRowsPerPage,
      );

      hookForm.setValue('itens', getAllInspecaoMaquinasEquipItemUseCase.items.map(item => ({
        idInspecaoMaquinaEquipItens: item.idInspecaoMaquinaEquipItens,
        ordem: item.ordem,
        descricao: item.descricao,
        inspecao1: item.inspecao1 ?? '',
        inspecao2: item.inspecao2 ?? '',
      })));

      pagination.setTotalItems(getAllInspecaoMaquinasEquipItemUseCase.totalItems);

    } catch (e: unknown) {
      throw new Error("Falha ao buscar itens da inspeção!");
    }
  }, [
    idControleCentroCusto,
    idInspecaoMaquinaEquip,
    getAllInspecaoMaquinasEquipItemUseCase,
    pagination,
    hookForm,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationControleCentroCustoId = params.get("idControleCentroCusto");
      const controleCentroCustoId = locationControleCentroCustoId ?? undefined;
      const locationInspecaoMaquinasEquipId = params.get("id");
      const inspecaoMaquinasEquipId = locationInspecaoMaquinasEquipId ?? undefined;

      if (!controleCentroCustoId || controleCentroCustoId.length === 0) {
        setErrorMessage("Falha ao recuperar id do controle de centro de custo!");
        return;
      }
      setIdControleCentroCusto(Number(controleCentroCustoId));

      if (!inspecaoMaquinasEquipId || inspecaoMaquinasEquipId.length === 0) {
        setErrorMessage("Falha ao recuperar id da inspeção da máquina/equipamento!");
        return;
      }
      setIdInspecaoMaquinaEquip(Number(inspecaoMaquinasEquipId));

      hookForm.reset();

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadInspecaoMaquinasEquip({ idControleCentroCusto: Number(controleCentroCustoId), idInspecaoMaquinaEquip: Number(inspecaoMaquinasEquipId) });
      await loadInspecaoMaquinasEquipItens({ idControleCentroCusto: Number(controleCentroCustoId), idInspecaoMaquinaEquip: Number(inspecaoMaquinasEquipId) });

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      await loadInspecaoMaquinasEquip();
      await loadInspecaoMaquinasEquipItens();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao recarregar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionariosDropDown,
    loadInspecaoMaquinasEquip,
    loadInspecaoMaquinasEquipItens,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(async () => {
    if (idControleCentroCusto) {
      backOrNavigate(-1, { alternativeRoute: `/MaquinasEquipItens_controle?id=${idControleCentroCusto}` });
    }
  }, [
    idControleCentroCusto,
    backOrNavigate,
  ]);

  const handleInspecaoMaquinasEquipSubmit = useCallback(async (form: InspecaoMaquinaEquipForm) => {
    if (idControleCentroCusto && idInspecaoMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updateInspecaoMaquinasEquipDetailUseCase.updateInspecaoMaquinasEquipDetail(
          idControleCentroCusto,
          idInspecaoMaquinaEquip,
          form.idFuncionarioInspecionado,
          form.idFuncionarioAprovado,
          form.status,
          form.dataEncerramento,
          form.descricaoProblema,
          form.correcaoProblema,
          form.arquivo1,
          form.arquivo2,
          form.limparArquivo1,
          form.limparArquivo2,
          form.itens,
        );

        await loadInspecaoMaquinasEquip();
        await loadInspecaoMaquinasEquipItens();

        pushInfoNotification('Sucesso', "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao salvar inspeção da máquina/equipamento!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idControleCentroCusto,
    idInspecaoMaquinaEquip,
    updateInspecaoMaquinasEquipDetailUseCase,
    loadInspecaoMaquinasEquip,
    loadInspecaoMaquinasEquipItens,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleChangeInspecao1 = useCallback(async (idInspecaoMaquinaEquipItem: number, value: string | number | null) => {
    const itens = hookForm.getValues().itens;
    if (itens) {
      const idx = itens.findIndex((i: any) => i.idInspecaoMaquinaEquipItens === idInspecaoMaquinaEquipItem);
      if (idx !== -1 && itens[idx]) {
        if (typeof value === "string") {
          const lastValue = itens[idx].inspecao1;
          itens[idx].inspecao1 = value;
          if (lastValue === 'C' || lastValue === 'P') {
            itens[idx].inspecao2 = '';
          }
          hookForm.setValue('itens', [...itens]);
        }
      }
    }
  }, [
    hookForm,
  ]);

  const handleChangeInspecao2 = useCallback(async (idInspecaoMaquinaEquipItem: number, value: string | number | null) => {
    const itens = hookForm.getValues().itens;
    if (itens) {
      const idx = itens.findIndex((i: any) => i.idInspecaoMaquinaEquipItens === idInspecaoMaquinaEquipItem);
      if (idx !== -1 && itens[idx]) {
        if (typeof value === "string") {
          itens[idx].inspecao2 = value;
          hookForm.setValue('itens', [...itens]);
        }
      }
    }
  }, [
    hookForm,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useMaquinasEquipItensVerificacaoPagination.setState({ page: page + 1 })
      await loadInspecaoMaquinasEquipItens({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao recarregar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoMaquinasEquipItens,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useMaquinasEquipItensVerificacaoPagination.setState({ page: 1, rowsPerPage })
      await loadInspecaoMaquinasEquipItens({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao recarregar página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadInspecaoMaquinasEquipItens,
    pushErrorNotification,
  ]);

  const handleInspecaoMaquinasEquipDownload = useCallback(async (numeroArquivo: number) => {
    if (idControleCentroCusto && idInspecaoMaquinaEquip) {
      try {
        setLoading(true);

        await downloadInspecaoMaquinaEquipArquivoUseCase.downloadInspecaoMaquinaEquipArquivo(
          idControleCentroCusto,
          idInspecaoMaquinaEquip,
          numeroArquivo,
        );

      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao recarregar página!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idControleCentroCusto,
    idInspecaoMaquinaEquip,
    downloadInspecaoMaquinaEquipArquivoUseCase,
    pushErrorNotification,
  ]);

  const handleInspecaoMaquinasEquipLimparArquivo = useCallback((numeroArquivo: number) => {
    if (window.confirm("Tem certeza que deseja excluir este arquivo?")) {
      switch (numeroArquivo) {
        case 1:
          hookForm.setValue('limparArquivo1', true);
          setInspecaoMaquinaEquip(prev => prev !== undefined ? ({ ...prev, nomearquivo1: null }) : undefined);
          break;
        case 2:
          hookForm.setValue('limparArquivo2', true);
          setInspecaoMaquinaEquip(prev => prev !== undefined ? ({ ...prev, nomearquivo2: null }) : undefined);
          break;
        default:
          break;
      }
    }
  }, [
    hookForm,
  ]);

  const handleOpenChangeHistory = useCallback(async (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idInspecaoMaquinaEquip) {
        changeHistory.handleOpenChangeHistory(idInspecaoMaquinaEquip, LogTipoTabela.INSPECAO_MAQUINA_EQUIP, true);
      }
    }
  }, [
    idInspecaoMaquinaEquip,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idInspecaoMaquinaEquip,
    inspecaoMaquinaEquip: inspecaoMaquinaEquip ?? null,
    inspecaoMaquinaEquipItens: inspecaoMaquinaEquipItensHook ?? [],
    funcionariosInspecionadoDropdown: funcionariosInspecionadoDropdown ?? [],
    funcionariosAprovadoDropdown: funcionariosAprovadoDropdown ?? [],
    hookForm,
    statusOptions,
    changeHistory,
    pagination,
    handleReload,
    handleBack,
    handleInspecaoMaquinasEquipSubmit: hookForm.handleSubmit(handleInspecaoMaquinasEquipSubmit),
    handleChangeInspecao1,
    handleChangeInspecao2,
    handlePageChange,
    handleRowsPerPageChange,
    handleInspecaoMaquinasEquipDownload,
    handleInspecaoMaquinasEquipLimparArquivo,
    handleOpenChangeHistory,
  };
};
